import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Print,
  ExpandMore,
  ExpandLess,
  UnfoldMore,
  UnfoldLess,
} from '@mui/icons-material';
import StandardPagination from './Pagination/StandardPagination';
import ConfirmModal from './modals/ConfirmModal';
import EvolutionModal from './modals/EvolutionModal';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import {
  patientEvolutionsService,
  mapPatientEvolutionError,
  PatientEvolution,
  PatientEvolutionAuthor,
  EVOLUTION_KINDS,
  EvolutionKind,
  evolutionKindLabel,
} from '../services/patientEvolutionsService';
import { colors } from '../theme/designSystem';
import ClearFiltersButton from './ClearFiltersButton';
import {
  filterFieldSx,
  searchFieldSx,
  dateFieldSx,
  filterActionButtonSx,
  resultCountSx,
} from '../theme/filterStyles';

/**
 * Aba Evoluções do cadastro do paciente: registro clínico por sessão, sobre a API real.
 * Lista em tabela, uma linha por evolução (data, profissional, atendimento, plano, nota), com
 * acordeão por linha e "abrir todas": o detalhe traz condição, justificativa, situação,
 * abordagem, recurso, evolução, conduta e observação (pedido do Hilton, 04/09/2026 — mesmo
 * desenho da tela que a equipe já usava). A lista vem ordenada (mais recente primeiro) e
 * paginada pelo servidor. Os textos só chegam a quem tem acesso (`canRead`); para os demais a
 * linha apenas informa que a evolução existe. Alteração e exclusão só pelo autor, na janela.
 */
interface Props {
  patientId: string;
}

const DEFAULT_PAGE_SIZE = 10;
const MIN_SEARCH_LENGTH = 3;
const SELECT_ALL_PAGE_SIZE = 100;
const EMPTY = '—';

const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const timeOf = (iso: string): string => iso.slice(11, 16);

const planLabel = (e: PatientEvolution): string => {
  if (e.disregardPlan) return 'Não avalia objetivo';
  if (!e.planTitle) return EMPTY;
  return e.planPeriodTitle ? `${e.planPeriodTitle} · ${e.planTitle}` : e.planTitle;
};

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  '&:hover': { backgroundColor: hover },
});

const cellSx = { display: 'flex', alignItems: 'center' };

/** Linha "rótulo: valor" do detalhe; valor vazio sai como travessão para a grade não pular. */
const DetailRow: React.FC<{ label: string; value: string | null | undefined; wide?: boolean }> = ({ label, value, wide }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0, gridColumn: wide ? '1 / -1' : 'auto' }}>
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: colors.textPrimary, whiteSpace: 'nowrap', minWidth: 96 }}>
      {label}:
    </Typography>
    <Typography sx={{ fontSize: '0.85rem', color: colors.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.6 }}>
      {value && value.trim() ? value : EMPTY}
    </Typography>
  </Box>
);

const PatientEvolutionsTab: React.FC<Props> = ({ patientId }) => {
  const [items, setItems] = useState<PatientEvolution[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [authors, setAuthors] = useState<PatientEvolutionAuthor[]>([]);
  const [editWindowDays, setEditWindowDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  // RN-542k: '' = todas
  const [kindFilter, setKindFilter] = useState<'' | EvolutionKind>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  const [editing, setEditing] = useState<PatientEvolution | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [allMode, setAllMode] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [toDelete, setToDelete] = useState<PatientEvolution | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { toast, showToast, hideToast } = useToast();
  const loadSeqRef = useRef(0);

  // Busca só a partir de 3 letras (abaixo disso a lista fica sem o filtro de texto)
  const effectiveSearch = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : '';
  const invalidRange = !!from && !!to && to < from;

  const load = useCallback(async () => {
    if (invalidRange) {
      setItems([]);
      setTotalCount(0);
      setError('A data final não pode ser anterior à data inicial.');
      setLoading(false);
      return;
    }
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError('');
    try {
      const result = await patientEvolutionsService.getByPatient(patientId, {
        search: effectiveSearch || undefined,
        createdBy: createdBy || undefined,
        kind: kindFilter || undefined,
        from: from || undefined,
        to: to || undefined,
        pageNumber: page,
        pageSize,
      });
      if (seq !== loadSeqRef.current) return;
      setItems(result.items);
      setTotalCount(result.totalCount);
      setAuthors(result.authors);
      setEditWindowDays(result.editWindowDays ?? 7);
    } catch (err: any) {
      if (seq !== loadSeqRef.current) return;
      // Sem relação com o paciente a API recusa com código próprio: a mensagem explica a régua
      setError(err?.response?.data?.code === 'PatientEvolution.NoRelation'
        ? mapPatientEvolutionError(err)
        : 'Não foi possível carregar as evoluções do paciente.');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [patientId, effectiveSearch, createdBy, kindFilter, from, to, page, pageSize, invalidRange]);

  useEffect(() => {
    load();
  }, [load]);

  const changeFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
    setSelected([]);
    setAllMode(false);
  };
  const hasFilters = !!search || !!createdBy || !!kindFilter || !!from || !!to;
  const clearFilters = () => {
    setSearch('');
    setCreatedBy('');
    setKindFilter('');
    setFrom('');
    setTo('');
    setPage(1);
    setSelected([]);
    setAllMode(false);
  };

  const openCreate = () => {
    setEditing(null);
    setModalKey(k => k + 1);
    setIsModalOpen(true);
  };
  const openEdit = (evolution: PatientEvolution) => {
    setEditing(evolution);
    setModalKey(k => k + 1);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEditing(null);
  };
  const handleSaved = (message: string) => {
    setIsModalOpen(false);
    setEditing(null);
    showToast(message, 'success');
    if (page === 1) load(); else setPage(1);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await patientEvolutionsService.remove(toDelete.id);
      showToast('Evolução excluída.', 'success');
      setSelected(prev => prev.filter(id => id !== toDelete.id));
      setAllMode(false);
      setToDelete(null);
      load();
    } catch (err: any) {
      showToast(mapPatientEvolutionError(err), 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Seleção para impressão: sobrevive à troca de página (zera ao mudar filtro); só o que se pode ler imprime
  // Seleção da página (cabeçalho da coluna): só o que pode ser lido entra
  const selectableIds = items.filter(e => e.canRead).map(e => e.id);
  const togglePageSelection = () => {
    const all = selectableIds.every(id => selected.includes(id));
    setSelected(prev => (all ? prev.filter(id => !selectableIds.includes(id)) : Array.from(new Set([...prev, ...selectableIds]))));
  };
  const toggleSelected = (id: string) => {
    setAllMode(false);
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };
  // "Selecionar todas" cobre TODAS as evoluções do filtro em vigor, não só a página: a API
  // pagina, então as demais páginas são buscadas na hora (pedido do Hilton, 04/09/2026)
  const [selectingAll, setSelectingAll] = useState(false);
  const allSelected = allMode && selected.length > 0;
  const someSelected = selected.length > 0 && !allSelected;
  const toggleSelectAll = async () => {
    if (allSelected || someSelected) {
      setSelected([]);
      setAllMode(false);
      return;
    }
    setSelectingAll(true);
    try {
      const ids: string[] = [];
      let pageNumber = 1;
      let total = Infinity;
      while ((pageNumber - 1) * SELECT_ALL_PAGE_SIZE < total) {
        const result = await patientEvolutionsService.getByPatient(patientId, {
          search: effectiveSearch || undefined,
          createdBy: createdBy || undefined,
          kind: kindFilter || undefined,
          from: from || undefined,
          to: to || undefined,
          pageNumber,
          pageSize: SELECT_ALL_PAGE_SIZE,
        });
        total = result.totalCount;
        ids.push(...result.items.filter(e => e.canRead).map(e => e.id));
        if (result.items.length === 0) break;
        pageNumber += 1;
      }
      setSelected(ids);
      setAllMode(true);
    } catch {
      showToast('Não foi possível selecionar todas as evoluções.', 'error');
    } finally {
      setSelectingAll(false);
    }
  };
  const openPrint = () => {
    window.open(`?page=evolutions-print&patientId=${patientId}&ids=${selected.join(',')}`, '_blank');
  };

  // Acordeão: por linha, e "abrir todas" sobre a página atual
  const isExpanded = (id: string) => expanded.includes(id);
  const toggleExpanded = (id: string) => {
    setExpanded(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };
  const allExpanded = items.length > 0 && items.every(e => expanded.includes(e.id));
  const toggleExpandAll = () => {
    setExpanded(allExpanded ? [] : items.map(e => e.id));
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const editTooltip = (e: PatientEvolution) => (e.canEdit
    ? 'Editar evolução'
    : e.isMine ? `Prazo de edição encerrado (${editWindowDays} dias)` : 'Somente quem escreveu pode alterar');
  const rowActions = (evolution: PatientEvolution) => (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                      <Tooltip title={editTooltip(evolution)} arrow>
                        <span>
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            disabled={!evolution.canEdit}
                            onClick={() => openEdit(evolution)}
                            sx={rowActionSx(colors.primary, colors.primaryHover)}
                          >
                            <Edit sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={deleteTooltip(evolution)} arrow>
                        <span>
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            disabled={!evolution.canEdit}
                            onClick={() => setToDelete(evolution)}
                            sx={rowActionSx(colors.error, '#c82333')}
                          >
                            <Delete sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
  );

  const deleteTooltip = (e: PatientEvolution) => (e.canEdit
    ? 'Excluir evolução'
    : e.isMine ? `Prazo de exclusão encerrado (${editWindowDays} dias)` : 'Somente quem escreveu pode excluir');

  return (
    <div className="tab-content-section">
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: '21px' }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder={`Digite ao menos ${MIN_SEARCH_LENGTH} letras...`}
          value={search}
          onChange={(e) => changeFilter(setSearch)(e.target.value)}
          inputProps={{ maxLength: 100 }}
          InputLabelProps={{ shrink: true }}
          sx={searchFieldSx}
        />
        <TextField
          select
          size="small"
          label="Autor"
          value={createdBy}
          onChange={(e) => changeFilter(setCreatedBy)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {authors.map(a => (
            <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Tipo"
          value={kindFilter}
          onChange={(e) => changeFilter(v => setKindFilter(v as '' | EvolutionKind))(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todas</MenuItem>
          {EVOLUTION_KINDS.map(k => (
            <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          size="small"
          label="De"
          value={from}
          onChange={(e) => changeFilter(setFrom)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <TextField
          type="date"
          size="small"
          label="Até"
          value={to}
          onChange={(e) => changeFilter(setTo)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
        <Tooltip
          title={selected.length === 0
            ? 'Selecione ao menos uma evolução para imprimir'
            : `Imprimir ${selected.length} ${selected.length === 1 ? 'evolução selecionada' : 'evoluções selecionadas'}`}
          arrow
        >
          <span style={{ marginLeft: 'auto' }}>
            <IconButton onClick={openPrint} disabled={selected.length === 0} sx={filterActionButtonSx}>
              <Print />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Nova evolução" arrow>
          <span>
            <IconButton onClick={openCreate} sx={filterActionButtonSx}>
              <Add />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ ...resultCountSx, px: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title={allSelected || someSelected ? 'Limpar seleção' : 'Selecionar todas as evoluções do filtro'} arrow>
          <span>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleSelectAll}
              disabled={totalCount === 0 || selectingAll || loading}
              sx={{ p: 0.25, color: colors.primary, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: colors.primary } }}
            />
          </span>
        </Tooltip>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          <strong>{totalCount}</strong> {totalCount === 1 ? 'evolução encontrada' : 'evoluções encontradas'}
          {selected.length > 0 && <> · <strong>{selected.length}</strong> selecionada{selected.length === 1 ? '' : 's'} para impressão</>}
        </Typography>
        <Tooltip title={allExpanded ? 'Fechar todas' : 'Abrir todas'} arrow>
          <span style={{ marginLeft: 'auto' }}>
            <IconButton
              size="small"
              onClick={toggleExpandAll}
              disabled={items.length === 0}
              sx={{ color: colors.primary }}
            >
              {allExpanded ? <UnfoldLess fontSize="small" /> : <UnfoldMore fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : error ? (
        <Typography sx={{ color: colors.error, fontSize: '0.9rem', px: 1, py: 2 }}>{error}</Typography>
      ) : items.length === 0 ? (
        <Typography sx={{ color: colors.textSecondary, fontSize: '0.9rem', textAlign: 'center', py: 4 }}>
          Nenhuma evolução encontrada para este paciente.
        </Typography>
      ) : (
        <Box className="c4u-table c4u-table--serial">
          {/* Ordem de sacrifício das colunas: atendimento some primeiro, depois plano e nota.
              Data, profissional, ver e ações ficam sempre. */}
          <Box className="c4u-table__header">
            <Box className="admin-plans-header-cell" sx={{ flex: '0 0 44px', justifyContent: 'center' }}>
              <Tooltip title="Selecionar todas desta página para impressão" arrow>
                <Checkbox
                  size="small"
                  checked={selectableIds.length > 0 && selectableIds.every(id => selected.includes(id))}
                  indeterminate={selectableIds.some(id => selected.includes(id)) && !selectableIds.every(id => selected.includes(id))}
                  disabled={selectableIds.length === 0}
                  onChange={togglePageSelection}
                  sx={{ p: 0.25, color: colors.primary, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: colors.primary } }}
                />
              </Tooltip>
            </Box>
            <Box className="admin-plans-header-cell" sx={{ flex: '0 0 130px' }}>Data</Box>
            <Box className="admin-plans-header-cell" sx={{ flex: '1 1 180px', minWidth: 140 }}>Profissional</Box>
            <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: '1 1 180px', minWidth: 140 }}>Atendimento</Box>
            <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: '1 1 200px', minWidth: 140 }}>Plano terapêutico</Box>
            <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: '0 0 70px' }}>Nota</Box>
            <Box className="admin-plans-header-cell" sx={{ flex: '0 0 56px', justifyContent: 'center' }}>Ver</Box>
            <Box className="admin-plans-header-cell" sx={{ flex: '0 0 110px', justifyContent: 'flex-end' }}>Ações</Box>
          </Box>

          <Box className="admin-plans-table-body">
            {items.map(evolution => (
              <React.Fragment key={evolution.id}>
                <Box className="c4u-table__row">
                  <Box className="admin-plans-cell" sx={{ ...cellSx, flex: '0 0 44px', justifyContent: 'center' }} data-label="Imprimir">
                    <Tooltip title={evolution.canRead ? 'Selecionar para impressão' : 'Conteúdo restrito: não imprime'} arrow>
                      <span>
                        <Checkbox
                          size="small"
                          checked={selected.includes(evolution.id)}
                          disabled={!evolution.canRead}
                          onChange={() => toggleSelected(evolution.id)}
                          sx={{ p: 0.5, color: colors.primary, '&.Mui-checked': { color: colors.primary } }}
                        />
                      </span>
                    </Tooltip>
                  </Box>
                  <Box className="admin-plans-cell" sx={{ ...cellSx, flex: '0 0 130px', gap: 0.75 }} data-label="Data">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatIsoDate(evolution.sessionDate)}</Typography>
                        {/* Avulsa não tem horário: a data é a do registro */}
                        {evolution.kind !== 'standalone' && (
                          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>{timeOf(evolution.sessionDate)}</Typography>
                        )}
                      </Box>
                      <Chip
                        label={evolution.kind === 'standalone' ? 'Avulsa' : 'De sessão'}
                        title={evolutionKindLabel(evolution.kind)}
                        size="small"
                        variant={evolution.kind === 'standalone' ? 'filled' : 'outlined'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </Box>
                  <Box className="admin-plans-cell" sx={{ ...cellSx, flex: '1 1 180px', minWidth: 140, '@media (max-width: 768px)': { display: 'grid' } }} data-label="Profissional">
                    <Typography sx={{ fontSize: 13, overflowWrap: 'anywhere' }}>
                      {evolution.professionalName || (evolution.kind === 'standalone' ? evolution.createdByName : EMPTY)}
                    </Typography>
                  </Box>
                  <Box className="admin-plans-cell c4u-col--lg" sx={{ ...cellSx, flex: '1 1 180px', minWidth: 140 }} data-label="Atendimento">
                    <Typography sx={{ fontSize: 13, overflowWrap: 'anywhere' }}>{evolution.serviceTypeName || EMPTY}</Typography>
                  </Box>
                  <Box className="admin-plans-cell c4u-col--md" sx={{ ...cellSx, flex: '1 1 200px', minWidth: 140 }} data-label="Plano terapêutico">
                    <Typography sx={{ fontSize: 13, overflowWrap: 'anywhere', color: evolution.disregardPlan ? colors.textSecondary : colors.text }}>
                      {evolution.kind === 'standalone' ? EMPTY : planLabel(evolution)}
                    </Typography>
                  </Box>
                  <Box className="admin-plans-cell c4u-col--md" sx={{ ...cellSx, flex: '0 0 70px' }} data-label="Nota">
                    <Typography sx={{ fontSize: 13 }}>
                      {evolution.planEvolutionScale === null ? EMPTY : evolution.planEvolutionScale}
                    </Typography>
                  </Box>
                  <Box className="admin-plans-cell" sx={{ ...cellSx, flex: '0 0 56px', justifyContent: 'center' }} data-label="Ver">
                    <Tooltip title={isExpanded(evolution.id) ? 'Fechar detalhes' : 'Ver detalhes'} arrow>
                      <IconButton size="small" onClick={() => toggleExpanded(evolution.id)} sx={{ color: colors.primary }}>
                        {isExpanded(evolution.id) ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {/* Celular: com o detalhe aberto, as ações vão para o fim dele (ler antes de agir) */}
                  <Box
                    className="admin-plans-cell admin-plans-actions"
                    sx={{ flex: '0 0 110px', ...(isExpanded(evolution.id) ? { '@media (max-width: 768px)': { display: 'none !important' } } : {}) }}
                    data-label="Ações"
                  >
                    {rowActions(evolution)}
                  </Box>
                </Box>

                {isExpanded(evolution.id) && (
                  <Box
                    className="c4u-table__expand"
                    sx={{
                      px: { xs: 1.5, md: 3 },
                      py: 2,
                      backgroundColor: '#f8f9fa',
                      borderBottom: '1px solid #e2e8f0',
                      mb: { xs: 1.5, md: 0 },
                    }}
                  >
                    {evolution.kind === 'standalone' ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 1 }}>
                        {evolution.canRead ? (
                          <DetailRow label="Evolução" value={evolution.therapeuticEvolution} wide />
                        ) : (
                          <Typography sx={{ fontSize: '0.85rem', color: colors.textMuted, fontStyle: 'italic' }}>
                            Conteúdo restrito ao autor e a quem tem acesso concedido a ele.
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>
                          Registrado por {evolution.createdByName} em {formatIsoDate(evolution.createdAt)} {timeOf(evolution.createdAt)}
                          {!evolution.canEdit && ' · Somente leitura'}
                        </Typography>
                      </Box>
                    ) : (
                    <>
                    {/* Bloco do plano isolado (linha própria): some quando a evolução não avalia o plano */}
                    {!evolution.disregardPlan && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' }, columnGap: 3, rowGap: 1, mb: 1.5, pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                        <DetailRow
                          label="Plano terapêutico"
                          value={[planLabel(evolution), evolution.planCurrentScale !== null && evolution.planTargetScale !== null
                            ? `(estado atual ${evolution.planCurrentScale} → meta ${evolution.planTargetScale})`
                            : null].filter(Boolean).join(' ')}
                        />
                        <DetailRow label="Nota da sessão" value={evolution.planEvolutionScale === null ? null : `${evolution.planEvolutionScale}/10`} />
                      </Box>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, columnGap: 3, rowGap: 1 }}>
                      <DetailRow label="Situação" value={evolution.patientSituationName} />
                      <DetailRow label="Condição" value={evolution.patientConditionName} />
                      {evolution.canRead && (
                        <DetailRow label="Justificativa" value={evolution.patientConditionJustification} wide />
                      )}
                      <DetailRow label="Abordagem" value={evolution.aproachName} />
                      <DetailRow label="Recurso" value={evolution.therapeuticResourceName} />
                      <DetailRow label="Escala GAS" value={evolution.gasScale} wide />
                      {evolution.canRead ? (
                        <>
                          <DetailRow label="Evolução" value={evolution.therapeuticEvolution} wide />
                          <DetailRow label="Conduta" value={evolution.guidance} wide />
                          <DetailRow label="Observação" value={evolution.observation} wide />
                        </>
                      ) : (
                        <Typography sx={{ gridColumn: '1 / -1', fontSize: '0.85rem', color: colors.textMuted, fontStyle: 'italic' }}>
                          Conteúdo restrito ao autor e a quem tem acesso concedido a ele.
                        </Typography>
                      )}
                      <Typography sx={{ gridColumn: '1 / -1', fontSize: '0.75rem', color: colors.textMuted }}>
                        Registrado por {evolution.createdByName} em {formatIsoDate(evolution.createdAt)} {timeOf(evolution.createdAt)}
                        {!evolution.canEdit && ' · Somente leitura'}
                      </Typography>
                    </Box>
                    </>
                    )}
                    <Box
                      sx={{
                        display: 'none',
                        '@media (max-width: 768px)': { display: 'flex' },
                        mt: 2, pt: 1.5, borderTop: '1px solid #e2e8f0',
                      }}
                    >
                      {rowActions(evolution)}
                    </Box>
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}

      {totalCount > 0 && !error && (
        <StandardPagination
          currentPage={page}
          totalPages={totalPages}
          itemsPerPage={pageSize}
          totalItems={totalCount}
          onPageChange={setPage}
          onItemsPerPageChange={(n) => { setPageSize(n); setPage(1); }}
        />
      )}

      {isModalOpen && (
        <EvolutionModal
          key={modalKey}
          isOpen={isModalOpen}
          patientId={patientId}
          evolution={editing}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}

      <ConfirmModal
        isOpen={!!toDelete}
        title="Excluir evolução"
        message="A evolução deixará de aparecer na lista e nas impressões. O registro é preservado."
        confirmButtonText="Excluir"
        type="danger"
        isProcessing={deleting}
        onConfirm={handleDelete}
        onCancel={() => { if (!deleting) setToDelete(null); }}
      />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </div>
  );
};

export default PatientEvolutionsTab;
